import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Clock, Send, LogOut, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { authGet, authPost, logout } from '../../utils/auth';

const PendingAccess = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [requesting, setRequesting] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // `location.state.user` is used ONLY as an optimistic placeholder so the
    // card can render instantly; it is NEVER trusted as the gate. The session
    // is always verified server-side below. (This page intentionally does not
    // use useRequireAdmin: it serves authenticated NON-admin users, whereas the
    // hook redirects non-admins to /admin/login — which would loop here.)
    if (location.state?.user) {
      setUser(location.state.user);
    }

    let active = true;
    const checkAuth = async () => {
      try {
        const response = await authGet('/auth/me');

        if (!active) return;
        if (response.data && response.data.user_id) {
          // Server says you're an admin — go to the dashboard.
          if (response.data.role === 'admin') {
            navigate('/admin', { replace: true });
            return;
          }
          setUser(response.data);
        } else {
          navigate('/admin/login', { replace: true });
        }
      } catch (error) {
        if (active) navigate('/admin/login', { replace: true });
      } finally {
        if (active) setLoading(false);
      }
    };

    checkAuth();
    return () => { active = false; };
  }, [navigate, location.state]);

  const handleRequestAccess = async () => {
    setRequesting(true);
    try {
      await authPost('/auth/request-access');
      
      setUser(prev => ({ ...prev, access_requested: true }));
      toast.success('Access request submitted');
    } catch (error) {
      toast.error('Failed to submit request');
    }
    setRequesting(false);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login', { replace: true });
    toast.success('Logged out successfully');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  const hasRequestedAccess = user?.access_requested;

  return (
    <div className="min-h-screen bg-muted flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="bg-card border border-border rounded-xl p-8 shadow-sm text-center">
          {/* User Avatar */}
          {user?.picture && (
            <img 
              src={user.picture} 
              alt={user.name} 
              className="w-20 h-20 rounded-full mx-auto mb-4 border-4 border-primary/20"
            />
          )}
          
          <h1 className="text-2xl font-heading font-bold text-foreground mb-2">
            Welcome, {user?.name?.split(' ')[0] || 'User'}
          </h1>
          <p className="text-sm text-muted-foreground mb-6">{user?.email}</p>
          
          {hasRequestedAccess ? (
            <>
              <div className="inline-flex items-center justify-center w-16 h-16 bg-yellow-100 dark:bg-yellow-900/30 rounded-full mb-4">
                <Clock className="w-8 h-8 text-yellow-600 dark:text-yellow-400" strokeWidth={1.5} />
              </div>
              <h2 className="text-xl font-semibold text-foreground mb-2">Request Pending</h2>
              <p className="text-muted-foreground mb-6">
                Your access request has been submitted. An admin will review your request shortly.
              </p>
              <div className="flex items-center justify-center gap-2 text-sm text-yellow-600 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg py-3 px-4">
                <CheckCircle className="w-4 h-4" />
                <span>Request submitted</span>
              </div>
            </>
          ) : (
            <>
              <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mb-4">
                <Clock className="w-8 h-8 text-primary" strokeWidth={1.5} />
              </div>
              <h2 className="text-xl font-semibold text-foreground mb-2">Access Required</h2>
              <p className="text-muted-foreground mb-6">
                You need admin approval to access the dashboard. Click below to request access.
              </p>
              <button
                onClick={handleRequestAccess}
                disabled={requesting}
                className="w-full bg-primary text-white hover:bg-primary-hover rounded-lg px-6 py-3 font-medium transition-all inline-flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {requesting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Requesting...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Request Access
                  </>
                )}
              </button>
            </>
          )}
          
          <button
            onClick={handleLogout}
            className="mt-6 text-sm text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1"
          >
            <LogOut className="w-4 h-4" />
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
};

export default PendingAccess;
